export const DEFAULT_STORAGE_DURATION_H = 4;
export const DEFAULT_PUMPED_STORAGE_DURATION_H = 6;

/** Select entire value when a numeric input receives focus (click or tab). */
export function selectInputValueOnFocus(event) {
  event.target.select();
}

export const SUPPLY_WEATHER_YEARS = [2010, 2013, 2015, 2019, 2021];

export function createEmptyInputsLive() {
  return {
    demandTwhInput: '',
    demandTwhError: '',
    solarInputGW: '',
    windInputGW: '',
    windOffInputGW: '',
    nuclearInputGW: '',
    hydroInputGW: '',
    bioInputGW: '',
    coalInputGW: '',
    geoInputGW: '',
    gasInputGW: '',
    oilInputGW: '',
    storageInputGW: '',
    storageDurationInput: String(DEFAULT_STORAGE_DURATION_H),
    pumpedStorageInputGW: '',
    pumpedStorageDurationInput: String(DEFAULT_PUMPED_STORAGE_DURATION_H),
    /** @type {Record<string, string>} catalog techId → GW draft string */
    voSubtechByTechId: {},
  };
}

const VO_FLEX_PARENT_FUELS = ['bio', 'coal', 'gas', 'oil'];

/**
 * Seed sub-fuel draft map from catalog-grouped capacity rows.
 * @param {Record<string, Array<{ techId?: string, capacityGw?: number }>>|null|undefined} voCapacityByFuel
 * @param {(n: number) => string} formatGw
 */
export function seedVoSubtechDraftFromCapacityByFuel(voCapacityByFuel, formatGw) {
  /** @type {Record<string, string>} */
  const out = {};
  if (!voCapacityByFuel || typeof formatGw !== 'function') return out;
  for (const fuel of VO_FLEX_PARENT_FUELS) {
    for (const row of voCapacityByFuel[fuel] || []) {
      if (!row?.techId) continue;
      const gw = Number(row.capacityGw);
      out[row.techId] = formatGw(Number.isFinite(gw) ? gw : 0);
    }
  }
  return out;
}

/**
 * Sum draft (or fallback capacity) GW for one parent fuel.
 * @param {Record<string, Array<{ techId?: string, capacityGw?: number }>>|null|undefined} voCapacityByFuel
 * @param {string} fuel
 * @param {Record<string, string>|null|undefined} draftByTechId
 */
export function sumVoSubtechDraftForFuel(voCapacityByFuel, fuel, draftByTechId) {
  const rows = voCapacityByFuel?.[fuel] || [];
  let sum = 0;
  for (const row of rows) {
    if (!row?.techId) continue;
    const raw = draftByTechId?.[row.techId];
    if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
      const n = parseFloat(String(raw));
      sum += Number.isFinite(n) ? Math.max(0, n) : 0;
    } else {
      const gw = Number(row.capacityGw);
      sum += Number.isFinite(gw) ? Math.max(0, gw) : 0;
    }
  }
  return sum;
}

/**
 * Nested expand/edit UI only when there are 2+ flex sub-fuels under a parent.
 * A single child adds no useful breakdown vs the parent GW field.
 * @param {unknown} rows
 */
export function hasExpandableVoSubfuels(rows) {
  return Array.isArray(rows) && rows.length >= 2;
}

/**
 * Parent field keys that map to voCapacityByFuel fuels.
 */
export const VO_SUBTECH_PARENT_FIELD = {
  bio: 'bioInputGW',
  coal: 'coalInputGW',
  gas: 'gasInputGW',
  oil: 'oilInputGW',
};

/**
 * @param {Record<string, string>|null|undefined} draftMap
 * @param {Record<string, string>|null|undefined} lastApplied
 * @returns {string[]} changed techIds
 */
export function getChangedVoSubtechTechIds(draftMap, lastApplied) {
  const draft = draftMap && typeof draftMap === 'object' ? draftMap : {};
  const last = lastApplied && typeof lastApplied === 'object' ? lastApplied : {};
  const keys = new Set([...Object.keys(draft), ...Object.keys(last)]);
  const changed = [];
  for (const k of keys) {
    const a = parseFloat(draft[k]);
    const b = parseFloat(last[k]);
    if (Number.isNaN(a) && Number.isNaN(b)) continue;
    if (Number.isNaN(a) !== Number.isNaN(b) || a !== b) changed.push(k);
  }
  return changed;
}

/**
 * @param {Record<string, string>|null|undefined} draftMap
 * @returns {Record<string, number>}
 */
export function parseVoSubtechDraftToApplied(draftMap) {
  /** @type {Record<string, number>} */
  const out = {};
  if (!draftMap || typeof draftMap !== 'object') return out;
  for (const [techId, raw] of Object.entries(draftMap)) {
    const n = parseFloat(String(raw ?? ''));
    out[techId] = Number.isFinite(n) && n > 0 ? n : 0;
  }
  return out;
}

export function syncInputsLiveRef(inputsLiveRef, draft) {
  Object.assign(inputsLiveRef.current, draft);
}

export function getDemandInputChanged(demandTwhInput, lastAppliedDemandTwhInput) {
  const current = parseFloat(demandTwhInput);
  const last = parseFloat(lastAppliedDemandTwhInput);
  if (Number.isNaN(current) && Number.isNaN(last)) return false;
  if (Number.isNaN(current) !== Number.isNaN(last)) return true;
  return current !== last;
}

export function getChangedCapacityKeys(live, lastAppliedCapacityInputs) {
  const current = {
    solar: live.solarInputGW,
    wind: live.windInputGW,
    windOff: live.windOffInputGW,
    nuclear: live.nuclearInputGW,
    hydro: live.hydroInputGW,
  };
  const last = lastAppliedCapacityInputs;
  const keys = [];
  for (const k of ['solar', 'wind', 'windOff', 'nuclear', 'hydro']) {
    const a = parseFloat(current[k]);
    const b = parseFloat(last[k]);
    if (Number.isNaN(a) && Number.isNaN(b)) continue;
    if (Number.isNaN(a) !== Number.isNaN(b) || a !== b) keys.push(k);
  }
  return keys;
}

export function getChangedDispatchableKeys(live, lastAppliedDispatchableInputs) {
  const current = {
    bio: live.bioInputGW,
    coal: live.coalInputGW,
    geo: live.geoInputGW,
    gas: live.gasInputGW,
    oil: live.oilInputGW,
    storage: live.storageInputGW,
    duration: live.storageDurationInput,
    pumpedStorage: live.pumpedStorageInputGW,
    pumpedDuration: live.pumpedStorageDurationInput,
  };
  const last = lastAppliedDispatchableInputs;
  const keys = [];
  for (const k of ['bio', 'coal', 'geo', 'gas', 'oil', 'storage', 'duration', 'pumpedStorage', 'pumpedDuration']) {
    const a = parseFloat(current[k]);
    const b = parseFloat(last[k]);
    if (Number.isNaN(a) && Number.isNaN(b)) continue;
    if (Number.isNaN(a) !== Number.isNaN(b) || a !== b) keys.push(k);
  }
  return keys;
}

export function getCapacityInputErrors(live, capacityLimits) {
  const errors = {};
  const checks = [
    { key: 'solar', label: 'Solar', value: live.solarInputGW, limit: capacityLimits.solar },
    { key: 'wind', label: 'WindOn', value: live.windInputGW, limit: capacityLimits.windOn },
    { key: 'windOff', label: 'WindOff', value: live.windOffInputGW, limit: capacityLimits.windOff },
  ];
  for (const { key, label, value, limit } of checks) {
    if (limit == null || !Number.isFinite(limit) || limit <= 0) continue;
    const numeric = parseFloat(value);
    if (!Number.isFinite(numeric)) continue;
    if (numeric > limit) {
      errors[key] = `${label} cannot exceed ${limit.toFixed(2)} GW (max for this country).`;
    }
  }
  return errors;
}

export function validateDemandTwhValue(value) {
  if (value === '') {
    return { value: '', error: '' };
  }
  const numericRegex = /^\d{0,10}(\.\d*)?$/;
  if (!numericRegex.test(value)) {
    return { value, error: 'Only numbers allowed (max 10 digits)' };
  }
  const numValue = parseFloat(value);
  if (!isNaN(numValue) && numValue <= 0) {
    return { value, error: 'Value must be greater than 0' };
  }
  if (!isNaN(numValue) && numValue > 99999) {
    return { value, error: 'Value must be between 0.1 and 99,999 TWh' };
  }
  return { value, error: '' };
}

export function canRunUnifiedGenerate({
  live,
  lastAppliedDemandTwhInput,
  lastAppliedCapacityInputs,
  lastAppliedDispatchableInputs,
  lastAppliedVoSubtech = null,
  capacityLimits,
  chartData,
  nuclearProfile,
  solarProfile,
  windProfile,
}) {
  const demandInputChanged = getDemandInputChanged(live.demandTwhInput, lastAppliedDemandTwhInput);
  const canRunDemand = !!live.demandTwhInput && !live.demandTwhError && demandInputChanged;
  const capacityKeys = getChangedCapacityKeys(live, lastAppliedCapacityInputs);
  const hydroDisabled = !chartData || !nuclearProfile || !solarProfile || !windProfile;
  const onlyHydroChanged = capacityKeys.length === 1 && capacityKeys[0] === 'hydro';
  const canRunCapacity = capacityKeys.length > 0 && !(onlyHydroChanged && hydroDisabled);
  const dispatchableKeys = getChangedDispatchableKeys(live, lastAppliedDispatchableInputs);
  const voSubtechChanged =
    getChangedVoSubtechTechIds(live.voSubtechByTechId, lastAppliedVoSubtech).length > 0;
  const canRunDispatchable = dispatchableKeys.length > 0 || voSubtechChanged;
  const capacityInputErrors = getCapacityInputErrors(live, capacityLimits);
  const capacityLimitsExceeded = Object.keys(capacityInputErrors).length > 0;
  const anyRunnable = canRunDemand || canRunCapacity || canRunDispatchable;
  return {
    canRunDemand,
    canRunCapacity,
    canRunDispatchable,
    anyRunnable,
    capacityLimitsExceeded,
    capacityInputErrors,
  };
}
